import { notifyApprovalRequired } from './approvalBridge';
import { requestOtp, closeOtp } from './authBridge';

// Shared admin-API response interceptors: the maker-checker approval prompt and
// the TOTP 2FA challenge. Both are keyed off a specific envelope `status` field,
// and both need to act on whichever request triggered them.
//
// The app talks to the admin API through several axios instances (utils.js
// `request`, services/axios.instance, survey/campaign service instances). An
// interceptor only runs on the instance it's attached to, so each instance must
// call `attachAuthInterceptors` — otherwise a guarded response from that instance
// is never intercepted. Verification and replay run on the *same* `instance`, so
// the original request's config (baseURL, headers) is preserved.

// Guard against multiple in-flight requests all firing the session-expiry
// handler simultaneously — only the first one should redirect.
let _sessionExpired = false;

const is2faRequired = (error) =>
  error.response &&
  error.response.data &&
  error.response.data.status === '2fa-required';

export const attachAuthInterceptors = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // 2FA challenge takes priority over the generic 401 session-expiry redirect.
      // The backend may return either 401 or 403 for an unverified 2FA session,
      // so we check the envelope `status` before reacting to the HTTP status code.
      if (is2faRequired(error)) {
        // Prompt for the authenticator code, verify it, then replay the original
        // request. A rejected code re-prompts in place (with the server's message)
        // so a typo doesn't abort the action — the user just tries again.
        let otpError = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const totp = await requestOtp(otpError);
          if (!totp) {
            // User dismissed the prompt.
            closeOtp();
            return Promise.reject({
              message: 'Two-factor authentication required.',
              status: '2fa-required',
            });
          }
          try {
            await instance.post('/2fa', { token: totp });
            closeOtp();
            // Retry the original request, on the instance that made it.
            return instance(error.config);
          } catch (otpErr) {
            // Surface the server's message verbatim and let them retry.
            otpError =
              (otpErr.response &&
                otpErr.response.data &&
                otpErr.response.data.message) ||
              'Invalid code. Please try again.';
          }
        }
      }

      // Any 401 that isn't a 2FA challenge means the session is gone — clear
      // stored auth and reload so index.js renders the LoginScreen gate. The flag
      // prevents multiple concurrent in-flight requests from each redirecting.
      if (error.response && error.response.status === 401 && !_sessionExpired) {
        _sessionExpired = true;
        localStorage.removeItem('auth');
        window.location.replace('/');
        return Promise.reject(error);
      }

      // Maker-checker entry point: a guarded action was refused because it needs
      // a second approver. Open the "Approval required" prompt instead of letting
      // this surface as a generic error, and reject with a flagged payload so the
      // original action's spinner stops without an error toast (see showError).
      if (
        error.response &&
        error.response.status === 403 &&
        error.response.data &&
        error.response.data.status === 'approval-required'
      ) {
        notifyApprovalRequired(error.response.data.meta || {});
        return Promise.reject({
          ...error.response.data,
          __approvalHandled: true,
        });
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export default attachAuthInterceptors;
